import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/** Used when the setting is missing, so the toast can never stay forever. */
const DEFAULT_DURATION = 4000;

/** Kept in step with the panel's transition in `snippets/cart-toast.liquid`. */
const EXIT_DURATION = 220;

/** How wide a thumbnail to ask the CDN for: the rendered 56px at 2x. */
const THUMBNAIL_WIDTH = 224;

/**
 * The confirmation that appears after something is added to the cart.
 *
 * It listens for `cart:update` on the document rather than being called by
 * whatever did the adding, so the purchase panel, a future quick-add and a
 * future cart drawer all get the same confirmation without knowing this exists.
 * Only an update carrying `items` is an addition — a quantity change on the cart
 * page carries none — which is what keeps the toast to the one moment it is
 * about.
 *
 * It shows what the cart said was added and never works anything out for itself:
 * the title and image come from the lines `/cart/add.js` returned, and the count
 * is formatted from the translations Liquid put on `Theme`.
 *
 * The bar along the bottom is the visible half of the same clock the timeout
 * runs on. Both are paused by a hover or a focus, so the toast cannot leave
 * while it is being read, and the bar cannot claim time the timer is not taking.
 *
 * @extends {Component}
 */
class CartToast extends Component {
  /** @type {number} */
  #timer = 0;

  /** @type {number} */
  #exitTimer = 0;

  /** Milliseconds of visible time left, decremented at each pause. */
  #remaining = 0;

  /** When the current unpaused stretch began. */
  #startedAt = 0;

  /** @type {number} */
  #duration = DEFAULT_DURATION;

  connectedCallback() {
    super.connectedCallback();

    this.#duration = Number(this.dataset.duration) || DEFAULT_DURATION;
    this.style.setProperty('--cart-toast-duration', `${this.#duration}ms`);

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    // Bound to the element rather than delegated: `pointerenter` and
    // `pointerleave` do not bubble.
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.removeEventListener('pointerenter', this.#pause);
    this.removeEventListener('pointerleave', this.#resume);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('focusout', this.#resume);

    clearTimeout(this.#timer);
    clearTimeout(this.#exitTimer);
  }

  /** The close button. */
  dismiss() {
    this.#hide();
  }

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    const detail = /** @type {{ detail?: { items?: unknown, itemCount?: number } }} */ (event).detail;
    const items = detail?.items;

    if (!Array.isArray(items) || items.length === 0) return;

    this.#show(items[0], detail?.itemCount ?? 0);
  };

  /**
   * @param {{ title?: string, product_title?: string, image?: string | null }} line
   * @param {number} count
   */
  #show(line, count) {
    const panel = this.querySelector('.cart-toast__panel');
    const title = this.querySelector('.cart-toast__title');
    const countLine = this.querySelector('.cart-toast__count');
    const media = this.querySelector('.cart-toast__media');
    const image = this.querySelector('.cart-toast__image');

    if (!(panel instanceof HTMLElement)) return;

    if (title) title.textContent = line.title || line.product_title || '';

    if (countLine) {
      const template = count === 1 ? Theme.translations.cart_in_cart_one : Theme.translations.cart_in_cart_other;

      countLine.textContent = (template ?? '').replace('{{ count }}', String(count));
    }

    if (media instanceof HTMLElement && image instanceof HTMLImageElement) {
      const source = line.image ? this.#sized(line.image, THUMBNAIL_WIDTH) : null;

      media.hidden = !source;
      if (source) image.src = source;
    }

    clearTimeout(this.#exitTimer);
    panel.hidden = false;

    // A second add while the first toast is still up restarts it rather than
    // stacking: the countdown animation only replays from a clean slate, and the
    // remaining time has to be the new toast's, not what was left of the old.
    this.classList.remove('cart-toast--open');
    void this.offsetWidth;
    this.classList.add('cart-toast--open');

    this.style.setProperty('--cart-toast-play', 'running');
    this.#remaining = this.#duration;
    this.#start();
  }

  #start() {
    clearTimeout(this.#timer);
    this.#startedAt = Date.now();
    this.#timer = window.setTimeout(this.#hide, this.#remaining);
  }

  #hide = () => {
    clearTimeout(this.#timer);

    if (!this.classList.contains('cart-toast--open')) return;

    this.classList.remove('cart-toast--open');

    // Held open until the fade has finished, because `hidden` would cut it off
    // mid-transition and the toast would vanish rather than leave.
    this.#exitTimer = window.setTimeout(() => {
      const panel = this.querySelector('.cart-toast__panel');

      if (panel instanceof HTMLElement) panel.hidden = true;
    }, EXIT_DURATION);
  };

  #pause = () => {
    if (!this.classList.contains('cart-toast--open')) return;

    clearTimeout(this.#timer);
    this.#remaining = Math.max(0, this.#remaining - (Date.now() - this.#startedAt));
    this.style.setProperty('--cart-toast-play', 'paused');
  };

  /** @param {Event} event */
  #resume = (event) => {
    if (!this.classList.contains('cart-toast--open')) return;

    // Focus moving between the toast's own controls leaves it neither entered
    // nor left, and a pointer still resting on it outranks either.
    if (event instanceof FocusEvent && event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    if (this.matches(':hover')) return;

    this.style.setProperty('--cart-toast-play', 'running');
    this.#start();
  };

  /**
   * Asks the CDN for the thumbnail at the size it is drawn rather than loading
   * the full product shot to paint it 56 pixels wide.
   *
   * @param {string} url
   * @param {number} width
   * @returns {string}
   */
  #sized(url, width) {
    try {
      const parsed = new URL(url, window.location.origin);

      parsed.searchParams.set('width', String(width));

      return parsed.href;
    } catch {
      return url;
    }
  }
}

if (!customElements.get('cart-toast')) {
  customElements.define('cart-toast', CartToast);
}
